import {
	faCircleQuestion,
	faEnvelope,
	faPhone,
	faUserTie,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { useSalesRepInfo } from '../../../hooks/useSalesRepInfo';
import { telHref } from '../../../lib/supportContact';
import { usePortalSiteStore } from '../../../stores/portalSiteStore';

/**
 * Get Help widget (PD-15235) — the 2.0 counterpart of the legacy shop's
 * "Get Help" panel: business hours, the active account's assigned
 * salesperson (name + email + phone), the brand's general sales contact,
 * and the storefront's customer-support contact, in one card.
 *
 * Data sources:
 * - Business hours + support phone/email: the portal site's supportContact
 *   (PD-14363), already in the site store from the public bootstrap.
 * - General sales phone/email: the portal site's salesContact (PD-15552),
 *   from the same bootstrap. Site-level, not per-account.
 * - Salesperson: GET /portal/account/sales-rep for the ACTIVE client —
 *   null when the site opts out (settings.showSalesRepInfo) or no rep is
 *   assigned, and the block hides. Refetched when the active client
 *   changes (multi-account users switch locations).
 *
 * Unset fields inside a configured block render as "-" (1.0 parity); a
 * block whose source is entirely unconfigured hides instead, and when
 * nothing at all is configured the whole widget hides rather than
 * rendering a card of dashes.
 *
 * Login-required — kept off anonymous layouts via AUTH_ONLY_WIDGET_TYPES;
 * the token guard here is the backstop.
 */
export function GetHelpWidget({
	title,
	plain = false,
}: {
	title?: string;
	/**
	 * Render the content rows only — no card chrome and no own header. For
	 * hosts that already provide both, like GetHelpModal, whose dialog title
	 * IS "Get Help" (a second framed card inside the modal read as odd
	 * spacing — owner feedback on PD-15235).
	 */
	plain?: boolean;
}): JSX.Element | null {
	const { t } = useTranslation();
	const { token } = usePortalAuth();
	const supportContact = usePortalSiteStore(
		(s) => s.site?.supportContact ?? null,
	);
	const salesContact = usePortalSiteStore((s) => s.site?.salesContact ?? null);
	const salesRep = useSalesRepInfo();

	if (token == null) {
		return null;
	}

	if (supportContact == null && salesContact == null && salesRep == null) {
		return null;
	}

	const dash = t('-');

	const rows = (
		<>
			<div>
				<div className="text-xs font-semibold text-brand-muted uppercase tracking-wider mb-1">
					{t('Business Hours')}
				</div>
				<div className="text-sm text-default">
					{supportContact?.businessHours != null
						? t(supportContact.businessHours)
						: dash}
				</div>
			</div>

			{salesRep != null && (
				<div>
					<div className="text-xs font-semibold text-brand-muted uppercase tracking-wider mb-1">
						{t('Sales Rep')}
					</div>
					<div className="flex items-center gap-2 text-sm text-default">
						<FontAwesomeIcon
							icon={faUserTie}
							className="text-brand-muted w-4 text-center"
						/>
						<span>{t(salesRep.name)}</span>
					</div>
					{salesRep.email != null && (
						<a
							href={`mailto:${salesRep.email}`}
							className="flex items-center gap-2 mt-1 text-sm text-portal-accent hover:opacity-80"
						>
							<FontAwesomeIcon icon={faEnvelope} className="w-4 text-center" />
							<span className="truncate">{t(salesRep.email)}</span>
						</a>
					)}
					{/* PD-15552 — the rep's direct line, omitted when they have
					    no number on file (the server sends null). */}
					{salesRep.phone != null && (
						<a
							href={telHref(salesRep.phone)}
							className="flex items-center gap-2 mt-1 text-sm text-portal-accent hover:opacity-80"
						>
							<FontAwesomeIcon icon={faPhone} className="w-4 text-center" />
							<span>{t(salesRep.phone)}</span>
						</a>
					)}
				</div>
			)}

			{/* General sales contact (PD-15552) — the brand's sales line,
			    independent of whichever rep owns the account. Hidden entirely
			    when the brand has not configured one, so storefronts that never
			    set it keep the card they have today. */}
			{salesContact != null && (
				<div>
					<div className="text-xs font-semibold text-brand-muted uppercase tracking-wider mb-1">
						{t('Sales')}
					</div>
					{salesContact.phone != null && (
						<a
							href={telHref(salesContact.phone)}
							className="flex items-center gap-2 text-sm text-portal-accent hover:opacity-80"
						>
							<FontAwesomeIcon icon={faPhone} className="w-4 text-center" />
							<span>{t(salesContact.phone)}</span>
						</a>
					)}
					{salesContact.email != null && (
						<a
							href={`mailto:${salesContact.email}`}
							className="flex items-center gap-2 mt-1 text-sm text-portal-accent hover:opacity-80"
						>
							<FontAwesomeIcon icon={faEnvelope} className="w-4 text-center" />
							<span className="truncate">{t(salesContact.email)}</span>
						</a>
					)}
				</div>
			)}

			<div>
				<div className="text-xs font-semibold text-brand-muted uppercase tracking-wider mb-1">
					{t('Customer Support')}
				</div>
				{supportContact?.phone == null && supportContact?.email == null && (
					<div className="text-sm text-default">{dash}</div>
				)}
				{supportContact?.phone != null && (
					<a
						href={telHref(supportContact.phone)}
						className="flex items-center gap-2 text-sm text-portal-accent hover:opacity-80"
					>
						<FontAwesomeIcon icon={faPhone} className="w-4 text-center" />
						<span>{t(supportContact.phone)}</span>
					</a>
				)}
				{supportContact?.email != null && (
					<a
						href={`mailto:${supportContact.email}`}
						className="flex items-center gap-2 mt-1 text-sm text-portal-accent hover:opacity-80"
					>
						<FontAwesomeIcon icon={faEnvelope} className="w-4 text-center" />
						<span className="truncate">{t(supportContact.email)}</span>
					</a>
				)}
			</div>
		</>
	);

	if (plain) {
		return <div className="flex flex-col gap-4">{rows}</div>;
	}

	return (
		<section className="w-full h-full flex flex-col bg-brand-surface border border-brand-border rounded-xl p-4 md:p-5">
			<header className="flex items-center gap-2 mb-3">
				<FontAwesomeIcon
					icon={faCircleQuestion}
					className="text-portal-accent"
				/>
				<h2 className="text-base md:text-lg font-semibold text-default">
					{t(title ?? 'Get Help')}
				</h2>
			</header>

			<div className="flex-1 flex flex-col gap-4">{rows}</div>
		</section>
	);
}
